import { useState, useEffect, useMemo } from 'react'
import { publicAPI } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorMessage from '../components/ErrorMessage'
import SearchBar from '../components/SearchBar'
import PostnummerFilter from '../components/PostnummerFilter'
import ExportButtons from '../components/ExportButtons'
import { ArrowUp, ArrowDown, ChevronsUpDown, Car, Calendar, ChevronRight, ArrowLeft, Layers } from 'lucide-react'
import { useVehicleFilter } from '../contexts/VehicleFilterContext'
import { ColumnDefinition } from '../utils/exportToCsv'
import ChangeIndicator, { ChangeCount } from '../components/ChangeIndicator'
// import PeriodInfoBanner from '../components/PeriodInfoBanner' // Replaced by QueryExplanationBox
import QueryExplanationBox, { buildExplanationText } from '../components/QueryExplanationBox'
import { useMaxDataDate } from '../hooks/useMaxDataDate'
import { formatNorwegianDate } from '../utils/dateUtils'
import { usePersistedState } from '../hooks/usePersistedState'
import { useMinePostnummer } from '../hooks/useMinePostnummer'
import { useMineMerker } from '../hooks/useMineMerker'
import { useMirrorSharedDates } from '../hooks/useMirrorSharedDates'
import { useTranslation } from 'react-i18next'
import { localizeApiDetail } from '../i18n/apiError'

// Helper function to safely parse numeric values that might be strings
const toNumber = (value: number | string | null | undefined): number => {
  if (value === null || value === undefined) return 0
  return typeof value === 'string' ? parseFloat(value) : value
}

// Period info from API
interface PeriodInfo {
  label: string
  start_date: string
  end_date: string
}

interface PeriodsInfo {
  current: PeriodInfo
  previous: PeriodInfo
  ytd_current: PeriodInfo | null
  ytd_previous: PeriodInfo | null
}

// Brand row from API (Level 1)
interface TotalStatsBrandRow {
  ranking: number | null
  brand: string
  current_count: number
  current_market_share: number
  previous_count: number
  previous_market_share: number
  change_count: number
  change_percent: number | null
  ytd_current_count: number | null
  ytd_current_market_share: number | null
  ytd_previous_count: number | null
  ytd_previous_market_share: number | null
  ytd_change_count: number | null
  ytd_change_percent: number | null
}

// Master model row from API (Level 2)
interface TotalStatsMasterModelRow {
  ranking: number | null
  master_model_id: number | null
  master_model: string
  brand: string
  current_count: number
  current_market_share: number
  previous_count: number
  previous_market_share: number
  change_count: number
  change_percent: number | null
  ytd_current_count: number | null
  ytd_current_market_share: number | null
  ytd_previous_count: number | null
  ytd_previous_market_share: number | null
  ytd_change_count: number | null
  ytd_change_percent: number | null
}

// Variant row from API (Level 3)
interface TotalStatsVariantRow {
  ranking: number | null
  model_variant: string
  current_count: number
  current_market_share: number
  previous_count: number
  previous_market_share: number
  change_count: number
  change_percent: number | null
  ytd_current_count: number | null
  ytd_current_market_share: number | null
  ytd_previous_count: number | null
  ytd_previous_market_share: number | null
  ytd_change_count: number | null
  ytd_change_percent: number | null
}

type OutputMode = 'year_to_date' | 'month_to_date' | 'last_month' | 'last_week' | 'last_year' | 'yesterday' | 'custom'

// Labels live in the analytics namespace (outputModes.<mode>), translated at render.
const OUTPUT_MODES: OutputMode[] = [
  'year_to_date', 'month_to_date', 'last_month', 'last_week', 'yesterday', 'last_year', 'custom',
]

// Generic row type for the comparison table
type ComparisonRow = {
  ranking: number | null
  current_count: number
  current_market_share: number
  previous_count: number
  previous_market_share: number
  change_count: number
  change_percent: number | null
  ytd_current_count: number | null
  ytd_current_market_share: number | null
  ytd_previous_count: number | null
  ytd_previous_market_share: number | null
  ytd_change_count: number | null
  ytd_change_percent: number | null
  [key: string]: any
}

export default function TotalStatistics() {
  const { t } = useTranslation('analytics')
  // Shared state
  const [activeTab, setActiveTab] = useState<'merke' | 'modell'>('merke')
  const [outputMode, setOutputMode] = usePersistedState<OutputMode>('bp:filter:outputMode', 'year_to_date')
  const [periods, setPeriods] = useState<PeriodsInfo | null>(null)
  const { vehicleFilter, getVarebilParam } = useVehicleFilter()
  const { activeMerker } = useMineMerker()
  const [postnummerFilterEnabled, setPostnummerFilterEnabled] = usePersistedState('total-statistics:postnummerFilterEnabled', false)
  const minePostnummer = useMinePostnummer(postnummerFilterEnabled)
  const [customStartDate, setCustomStartDate] = usePersistedState<string>('bp:filter:customStartDate', '')
  const [customEndDate, setCustomEndDate] = usePersistedState<string>('bp:filter:customEndDate', '')
  const maxDataDate = useMaxDataDate()

  // "Last queried" filter values — updated only when an API fetch actually executes.
  // The explanation box and exports use these so they stay in sync with the displayed data.
  const [queriedOutputMode, setQueriedOutputMode] = useState<string>(outputMode)
  const [queriedVehicleFilter, setQueriedVehicleFilter] = useState<string>(vehicleFilter)
  const [queriedPostnummer, setQueriedPostnummer] = useState<string | null>(minePostnummer)

  const hasYtd = outputMode !== 'year_to_date' && outputMode !== 'custom' && outputMode !== 'last_year'

  // Mirror outputMode → shared startDate/endDate session keys so other analytics
  // pages (which read those keys via useYearDates) see the same date range
  // without the user having to click Oppdater.
  useMirrorSharedDates(outputMode, customStartDate, customEndDate, maxDataDate)

  // If sessionStorage restored 'custom' mode without dates, the initial fetch is
  // skipped — start with brandsLoading=false so the page renders instead of spinning.
  const initialFetchBlocked = outputMode === 'custom' && (!customStartDate || !customEndDate)

  // Level 1: Brands (Merke tab)
  const [brandsLoading, setBrandsLoading] = useState(!initialFetchBlocked)
  const [brandsError, setBrandsError] = useState<string | null>(null)
  const [brandsData, setBrandsData] = useState<TotalStatsBrandRow[]>([])
  const [brandsSummary, setBrandsSummary] = useState<TotalStatsBrandRow | null>(null)
  const [brandsSearch, setBrandsSearch] = useState('')
  const [brandsSortConfig, setBrandsSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null)

  // Level 2: Master Models within a brand (Merke tab drill-down)
  const [selectedBrand, setSelectedBrand] = useState<string | null>(null)
  const [masterModelsLoading, setMasterModelsLoading] = useState(false)
  const [masterModelsError, setMasterModelsError] = useState<string | null>(null)
  const [masterModelsData, setMasterModelsData] = useState<TotalStatsMasterModelRow[]>([])
  const [masterModelsSummary, setMasterModelsSummary] = useState<TotalStatsMasterModelRow | null>(null)
  const [masterModelsSearch, setMasterModelsSearch] = useState('')
  const [masterModelsSortConfig, setMasterModelsSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null)

  // Level 3: Variants within a master model (Merke tab drill-down)
  const [selectedMasterModel, setSelectedMasterModel] = useState<{ id: number; name: string } | null>(null)
  const [variantsLoading, setVariantsLoading] = useState(false)
  const [variantsError, setVariantsError] = useState<string | null>(null)
  const [variantsData, setVariantsData] = useState<TotalStatsVariantRow[]>([])
  const [variantsSummary, setVariantsSummary] = useState<TotalStatsVariantRow | null>(null)
  const [variantsSearch, setVariantsSearch] = useState('')
  const [variantsSortConfig, setVariantsSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null)

  // Modell tab: All models globally
  const [allModelsLoading, setAllModelsLoading] = useState(false)
  const [allModelsError, setAllModelsError] = useState<string | null>(null)
  const [allModelsData, setAllModelsData] = useState<TotalStatsMasterModelRow[]>([])
  const [, setAllModelsSummary] = useState<TotalStatsMasterModelRow | null>(null)
  const [allModelsSearch, setAllModelsSearch] = useState('')
  const [allModelsSortConfig, setAllModelsSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null)

  // Modell tab: Variant drill-down
  const [selectedModelAll, setSelectedModelAll] = useState<{ id: number; name: string } | null>(null)
  const [allVariantsLoading, setAllVariantsLoading] = useState(false)
  const [allVariantsError, setAllVariantsError] = useState<string | null>(null)
  const [allVariantsData, setAllVariantsData] = useState<TotalStatsVariantRow[]>([])
  const [allVariantsSummary, setAllVariantsSummary] = useState<TotalStatsVariantRow | null>(null)
  const [allVariantsSearch, setAllVariantsSearch] = useState('')
  const [allVariantsSortConfig, setAllVariantsSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null)

  // Build common params + snapshot queried filter values
  const getCommonParams = () => {
    const varebilParam = getVarebilParam()
    const params: any = { output_mode: outputMode }
    if (varebilParam !== undefined) params.varebil = varebilParam
    if (minePostnummer) params.mine_postnummer = minePostnummer
    if (outputMode === 'custom' && customStartDate && customEndDate) {
      params.custom_start_date = customStartDate
      params.custom_end_date = customEndDate
    }
    // Snapshot the filter values so the explanation box stays in sync with the data
    setQueriedOutputMode(outputMode)
    setQueriedVehicleFilter(vehicleFilter)
    setQueriedPostnummer(minePostnummer)
    return params
  }

  // Fetch Level 1
  const fetchBrands = async () => {
    try {
      setBrandsLoading(true)
      setBrandsError(null)
      const params = { ...getCommonParams(), limit: 100 }
      const response = await publicAPI.getTotalStatsBrands(params)
      const result = response.data
      setPeriods(result.periods)
      const [summaryRow, ...rows] = result.data
      setBrandsSummary(summaryRow || null)
      setBrandsData(rows)
    } catch (err: any) {
      setBrandsError(localizeApiDetail(err.response?.data?.detail) || err.message || t('common.loadFailed'))
    } finally {
      setBrandsLoading(false)
    }
  }

  // Fetch Level 2
  const fetchMasterModels = async (brand: string) => {
    try {
      setMasterModelsLoading(true)
      setMasterModelsError(null)
      const params = { ...getCommonParams(), brand, limit: 100 }
      const response = await publicAPI.getTotalStatsMasterModels(params)
      const result = response.data
      setPeriods(result.periods)
      const [summaryRow, ...rows] = result.data
      setMasterModelsSummary(summaryRow || null)
      setMasterModelsData(rows)
    } catch (err: any) {
      setMasterModelsError(localizeApiDetail(err.response?.data?.detail) || err.message || t('common.loadFailed'))
    } finally {
      setMasterModelsLoading(false)
    }
  }

  // Fetch Level 3
  const fetchVariants = async (masterModelId: number) => {
    try {
      setVariantsLoading(true)
      setVariantsError(null)
      const params = { ...getCommonParams(), master_model_id: masterModelId, limit: 100 }
      const response = await publicAPI.getTotalStatsVariants(params)
      const result = response.data
      setPeriods(result.periods)
      const [summaryRow, ...rows] = result.data
      setVariantsSummary(summaryRow || null)
      setVariantsData(rows)
    } catch (err: any) {
      setVariantsError(localizeApiDetail(err.response?.data?.detail) || err.message || t('common.loadFailed'))
    } finally {
      setVariantsLoading(false)
    }
  }

  // Fetch all models globally (Modell tab)
  const fetchAllModels = async () => {
    try {
      setAllModelsLoading(true)
      setAllModelsError(null)
      const params = { ...getCommonParams(), limit: 400 }
      const response = await publicAPI.getTotalStatsMasterModels(params)
      const result = response.data
      setPeriods(result.periods)
      const [summaryRow, ...rows] = result.data
      setAllModelsSummary(summaryRow || null)
      setAllModelsData(rows)
    } catch (err: any) {
      setAllModelsError(localizeApiDetail(err.response?.data?.detail) || err.message || t('common.loadFailed'))
    } finally {
      setAllModelsLoading(false)
    }
  }

  // Fetch variants for a model on Modell tab
  const fetchAllVariants = async (masterModelId: number) => {
    try {
      setAllVariantsLoading(true)
      setAllVariantsError(null)
      const params = { ...getCommonParams(), master_model_id: masterModelId, limit: 100 }
      const response = await publicAPI.getTotalStatsVariants(params)
      const result = response.data
      setPeriods(result.periods)
      const [summaryRow, ...rows] = result.data
      setAllVariantsSummary(summaryRow || null)
      setAllVariantsData(rows)
    } catch (err: any) {
      setAllVariantsError(localizeApiDetail(err.response?.data?.detail) || err.message || t('common.loadFailed'))
    } finally {
      setAllVariantsLoading(false)
    }
  }

  // Skip auto-fetches when 'custom' is selected without both dates — the
  // backend rejects the request, and the user must apply dates manually.
  const customModeMissingDates = outputMode === 'custom' && (!customStartDate || !customEndDate)

  // Re-fetch current level on output mode / vehicle filter change
  useEffect(() => {
    if (customModeMissingDates) return
    if (activeTab === 'merke') {
      if (selectedBrand && selectedMasterModel) {
        fetchVariants(selectedMasterModel.id)
      } else if (selectedBrand) {
        fetchMasterModels(selectedBrand)
      } else {
        fetchBrands()
      }
    } else {
      // Always fetch brands too so brandsSummary stays in sync for the TOTAL row
      fetchBrands()
      if (selectedModelAll) {
        fetchAllVariants(selectedModelAll.id)
      } else {
        fetchAllModels()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outputMode, vehicleFilter])

  // Initial fetch
  useEffect(() => {
    if (customModeMissingDates) return
    fetchBrands()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Tab switch handler
  const handleTabSwitch = (tab: 'merke' | 'modell') => {
    setActiveTab(tab)
    if (tab === 'modell' && !allModelsData.length && !allModelsLoading) {
      fetchAllModels()
    }
  }

  // Navigation handlers (Merke tab)
  const handleBrandClick = (brand: string) => {
    setSelectedBrand(brand)
    setSelectedMasterModel(null)
    setMasterModelsSearch('')
    setMasterModelsSortConfig(null)
    fetchMasterModels(brand)
  }

  const handleMasterModelClick = (mm: TotalStatsMasterModelRow) => {
    if (mm.master_model_id === null) return
    setSelectedMasterModel({ id: mm.master_model_id, name: mm.master_model })
    setVariantsSearch('')
    setVariantsSortConfig(null)
    fetchVariants(mm.master_model_id)
  }

  const handleBackToBrands = () => {
    setSelectedBrand(null)
    setSelectedMasterModel(null)
    setMasterModelsData([])
    setVariantsData([])
  }

  const handleBackToMasterModels = () => {
    setSelectedMasterModel(null)
    setVariantsData([])
  }

  // Navigation handlers (Modell tab)
  const handleModelAllClick = (mm: TotalStatsMasterModelRow) => {
    if (mm.master_model_id === null) return
    setSelectedModelAll({ id: mm.master_model_id, name: mm.master_model })
    setAllVariantsSearch('')
    setAllVariantsSortConfig(null)
    fetchAllVariants(mm.master_model_id)
  }

  const handleBackToAllModels = () => {
    setSelectedModelAll(null)
    setAllVariantsData([])
  }

  // ====== GENERIC SORT / FILTER / EXPORT HELPERS ======

  const createSortHandler = (
    sortConfig: { key: string; direction: 'asc' | 'desc' } | null,
    setSortConfig: (val: { key: string; direction: 'asc' | 'desc' } | null) => void
  ) => (key: string) => {
    if (sortConfig?.key === key) {
      if (sortConfig.direction === 'asc') {
        setSortConfig({ key, direction: 'desc' })
      } else {
        setSortConfig(null)
      }
    } else {
      setSortConfig({ key, direction: 'asc' })
    }
  }

  const createSortIconGetter = (
    sortConfig: { key: string; direction: 'asc' | 'desc' } | null
  ) => (columnKey: string) => {
    if (sortConfig?.key === columnKey) {
      return sortConfig.direction === 'asc' ?
        <ArrowUp className="w-4 h-4 inline ml-1" /> :
        <ArrowDown className="w-4 h-4 inline ml-1" />
    }
    return <ChevronsUpDown className="w-4 h-4 inline ml-1 opacity-40" />
  }

  const sortData = <T extends ComparisonRow>(
    data: T[],
    sortConfig: { key: string; direction: 'asc' | 'desc' } | null,
    nameField: string
  ): T[] => {
    if (!sortConfig) return data
    const sorted = [...data].sort((a, b) => {
      let aVal: any, bVal: any
      switch (sortConfig.key) {
        case 'ranking':
          aVal = a.ranking !== null ? a.ranking : Infinity
          bVal = b.ranking !== null ? b.ranking : Infinity
          break
        case nameField:
          aVal = (a[nameField] || '').toLowerCase()
          bVal = (b[nameField] || '').toLowerCase()
          break
        case 'change_percent':
        case 'ytd_change_percent':
          aVal = a[sortConfig.key] !== null ? toNumber(a[sortConfig.key]) : -Infinity
          bVal = b[sortConfig.key] !== null ? toNumber(b[sortConfig.key]) : -Infinity
          break
        default:
          aVal = toNumber(a[sortConfig.key])
          bVal = toNumber(b[sortConfig.key])
          break
      }
      if (typeof aVal === 'string') {
        return sortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
      }
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal
    })
    return sorted
  }

  const getExportColumns = (nameKey: string, nameLabel: string, showBrand?: boolean): ColumnDefinition[] => {
    const cols: ColumnDefinition[] = [
      { key: 'ranking', label: 'Ranking' },
      { key: nameKey, label: nameLabel },
      ...(showBrand ? [{ key: 'brand', label: t('common.brand') }] : []),
      { key: 'current_count', label: periods?.current.label || t('total.export.current'), format: (val) => String(toNumber(val)) },
      { key: 'current_market_share', label: t('total.export.shareOf', { label: periods?.current.label || t('total.export.nowLower') }), format: (val) => toNumber(val).toFixed(1) },
      { key: 'previous_count', label: periods?.previous.label || t('total.previous'), format: (val) => String(toNumber(val)) },
      { key: 'previous_market_share', label: t('total.export.shareOf', { label: periods?.previous.label || t('total.export.previousLower') }), format: (val) => toNumber(val).toFixed(1) },
      { key: 'change_count', label: t('common.change'), format: (val) => String(toNumber(val)) },
      { key: 'change_percent', label: t('common.changePct'), format: (val) => val !== null ? toNumber(val).toFixed(1) : '-' },
    ]
    if (hasYtd) {
      cols.push(
        { key: 'ytd_current_count', label: periods?.ytd_current?.label || t('total.ytdNow'), format: (val) => val !== null ? String(toNumber(val)) : '-' },
        { key: 'ytd_previous_count', label: periods?.ytd_previous?.label || t('total.ytdPrevious'), format: (val) => val !== null ? String(toNumber(val)) : '-' },
        { key: 'ytd_change_count', label: t('total.export.ytdChange'), format: (val) => val !== null ? String(toNumber(val)) : '-' },
        { key: 'ytd_change_percent', label: t('total.export.ytdChangePct'), format: (val) => val !== null ? toNumber(val).toFixed(1) : '-' }
      )
    }
    return cols
  }

  // ====== COMPUTED DATA ======

  // Level 1
  const filteredBrands = useMemo(() => {
    if (!brandsSearch) return brandsData
    return brandsData.filter(r => r.brand?.toLowerCase().includes(brandsSearch.toLowerCase()))
  }, [brandsData, brandsSearch])

  const sortedBrands = useMemo(() =>
    sortData(filteredBrands, brandsSortConfig, 'brand'),
    [filteredBrands, brandsSortConfig]
  )

  // Level 2
  const filteredMasterModels = useMemo(() => {
    if (!masterModelsSearch) return masterModelsData
    return masterModelsData.filter(r => r.master_model?.toLowerCase().includes(masterModelsSearch.toLowerCase()))
  }, [masterModelsData, masterModelsSearch])

  const sortedMasterModels = useMemo(() =>
    sortData(filteredMasterModels, masterModelsSortConfig, 'master_model'),
    [filteredMasterModels, masterModelsSortConfig]
  )

  // Level 3
  const filteredVariants = useMemo(() => {
    if (!variantsSearch) return variantsData
    return variantsData.filter(r => r.model_variant?.toLowerCase().includes(variantsSearch.toLowerCase()))
  }, [variantsData, variantsSearch])

  const sortedVariants = useMemo(() =>
    sortData(filteredVariants, variantsSortConfig, 'model_variant'),
    [filteredVariants, variantsSortConfig]
  )

  // Modell tab: All models
  const filteredAllModels = useMemo(() => {
    if (!allModelsSearch) return allModelsData
    return allModelsData.filter(r => r.master_model?.toLowerCase().includes(allModelsSearch.toLowerCase()))
  }, [allModelsData, allModelsSearch])

  const sortedAllModels = useMemo(() =>
    sortData(filteredAllModels, allModelsSortConfig, 'master_model'),
    [filteredAllModels, allModelsSortConfig]
  )

  // Modell tab: Variants
  const filteredAllVariants = useMemo(() => {
    if (!allVariantsSearch) return allVariantsData
    return allVariantsData.filter(r => r.model_variant?.toLowerCase().includes(allVariantsSearch.toLowerCase()))
  }, [allVariantsData, allVariantsSearch])

  const sortedAllVariants = useMemo(() =>
    sortData(filteredAllVariants, allVariantsSortConfig, 'model_variant'),
    [filteredAllVariants, allVariantsSortConfig]
  )

  const isLoading = activeTab === 'modell'
    ? (selectedModelAll ? allVariantsLoading : allModelsLoading)
    : (selectedMasterModel ? variantsLoading
      : selectedBrand ? masterModelsLoading
      : brandsLoading)

  const currentError = activeTab === 'modell'
    ? (selectedModelAll ? allVariantsError : allModelsError)
    : (selectedMasterModel ? variantsError
      : selectedBrand ? masterModelsError
      : brandsError)

  // ====== SHARED UI COMPONENTS ======

  const renderComparisonTable = <T extends ComparisonRow>(
    rows: T[],
    summary: ComparisonRow | null,
    nameField: string,
    nameLabel: string,
    searchValue: string,
    onSearchChange: (v: string) => void,
    searchPlaceholder: string,
    sortConfig: { key: string; direction: 'asc' | 'desc' } | null,
    setSortConfig: (v: { key: string; direction: 'asc' | 'desc' } | null) => void,
    exportTitle: string,
    onRowClick?: (row: T) => void,
    actionLabel?: string,
    loading?: boolean,
    showBrandColumn?: boolean,
  ) => {
    const handleSort = createSortHandler(sortConfig, setSortConfig)
    const getSortIcon = createSortIconGetter(sortConfig)

    return (
      <div className="card p-6">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            {sortConfig && (
              <button onClick={() => setSortConfig(null)} className="btn-secondary text-sm">
                {t('common.resetSort')}
              </button>
            )}
          </div>
          <ExportButtons
            data={summary ? [{ ...summary, ranking: '\u2211', [nameField]: 'TOTAL' } as unknown as T, ...rows] : rows}
            columns={getExportColumns(nameField, nameLabel, showBrandColumn)}
            metadata={{
              title: exportTitle,
              dateRange: periods ? t('common.dateRange', { start: formatNorwegianDate(periods.current.start_date), end: formatNorwegianDate(periods.current.end_date) }) : '',
              filters: [
                vehicleFilter === 'personbil' ? t('common.filterPersonbil') :
                  vehicleFilter === 'varebil' ? t('common.filterVarebil') : '',
                minePostnummer ? t('common.postnummerFilter', { value: minePostnummer }) : ''
              ].filter(f => f),
              explanationText: buildExplanationText({
                periods,
                maxDataDate,
                outputMode: queriedOutputMode,
                vehicleFilter: queriedVehicleFilter as 'personbil' | 'varebil' | 'alle',
                postnummerFilter: queriedPostnummer,
                drillDownLabel:
                  activeTab === 'merke'
                    ? selectedMasterModel
                      ? t('total.variantsFor', { name: `${selectedBrand} ${selectedMasterModel.name}` })
                      : selectedBrand
                        ? t('total.modelsFor', { brand: selectedBrand })
                        : null
                    : selectedModelAll
                      ? t('total.variantsFor', { name: selectedModelAll.name })
                      : null,
              }),
            }}
            disabled={loading}
          />
        </div>

        <div className="mb-4">
          <SearchBar value={searchValue} onChange={onSearchChange} placeholder={searchPlaceholder} />
        </div>

        <div className="overflow-x-auto">
          <table className="data-table data-table-compact">
            <thead>
              <tr>
                <th className="text-center cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('ranking')}>
                  # {getSortIcon('ranking')}
                </th>
                {showBrandColumn && (
                  <th className="text-left cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('brand')}>
                    {t('common.brand')} {getSortIcon('brand')}
                  </th>
                )}
                <th className="text-left max-w-[180px] cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort(nameField)}>
                  {nameLabel} {getSortIcon(nameField)}
                </th>
                <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('current_count')}>
                  {periods?.current.label || t('total.now')} {getSortIcon('current_count')}
                </th>
                <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 border-r-2 border-gray-300 dark:border-gray-600" onClick={() => handleSort('current_market_share')}>
                  {t('total.share')} {getSortIcon('current_market_share')}
                </th>
                <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('previous_count')}>
                  {periods?.previous.label || t('total.previous')} {getSortIcon('previous_count')}
                </th>
                <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 border-r-2 border-gray-300 dark:border-gray-600" onClick={() => handleSort('previous_market_share')}>
                  {t('total.share')} {getSortIcon('previous_market_share')}
                </th>
                <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('change_count')}>
                  +/- {getSortIcon('change_count')}
                </th>
                <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('change_percent')}>
                  {t('common.changePct')} {getSortIcon('change_percent')}
                </th>
                {hasYtd && (
                  <>
                    <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 border-l-2 border-gray-300 dark:border-gray-600" onClick={() => handleSort('ytd_current_count')}>
                      {periods?.ytd_current?.label || t('total.ytdNow')} {getSortIcon('ytd_current_count')}
                    </th>
                    <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800">
                      {periods?.ytd_previous?.label || t('total.ytdPrevious')}
                    </th>
                    <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('ytd_change_percent')}>
                      {t('total.ytdChangePctHeader')} {getSortIcon('ytd_change_percent')}
                    </th>
                  </>
                )}
                {onRowClick && <th className="text-center">{t('common.action')}</th>}
              </tr>
            </thead>
            <tbody>
              {/* Summary Row */}
              {summary && (
                <tr className="bg-primary-50 dark:bg-primary-900/20 font-bold border-t border-b border-primary-300 dark:border-primary-600">
                  <td className="text-center py-2"><span className="text-primary-600 dark:text-primary-400 font-bold">∑</span></td>
                  {showBrandColumn && <td className="py-2"></td>}
                  <td className="max-w-[180px] truncate font-bold text-primary-700 dark:text-primary-300 py-2">TOTAL</td>
                  <td className="text-right font-bold py-2">{toNumber(summary.current_count).toLocaleString('nb-NO')}</td>
                  <td className="text-right py-2 border-r-2 border-gray-300 dark:border-gray-600"><span className="font-bold text-primary-600 dark:text-primary-400">100,0</span></td>
                  <td className="text-right font-bold py-2">{toNumber(summary.previous_count).toLocaleString('nb-NO')}</td>
                  <td className="text-right py-2 border-r-2 border-gray-300 dark:border-gray-600"><span className="font-bold text-primary-600 dark:text-primary-400">100,0</span></td>
                  <td className="text-right font-bold py-2"><ChangeCount value={toNumber(summary.change_count)} /></td>
                  <td className="text-right font-bold py-2"><ChangeIndicator value={summary.change_percent} showIcon={false} /></td>
                  {hasYtd && (
                    <>
                      <td className="text-right font-bold py-2 border-l-2 border-gray-300 dark:border-gray-600">{toNumber(summary.ytd_current_count).toLocaleString('nb-NO')}</td>
                      <td className="text-right font-bold py-2">{toNumber(summary.ytd_previous_count).toLocaleString('nb-NO')}</td>
                      <td className="text-right font-bold py-2"><ChangeIndicator value={summary.ytd_change_percent} showIcon={false} /></td>
                    </>
                  )}
                  {onRowClick && <td className="text-center py-2"><span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">—</span></td>}
                </tr>
              )}

              {/* Data Rows */}
              {rows.map((row, idx) => (
                <tr
                  key={`${row[nameField]}-${idx}`}
                  className={`hover:bg-gray-100 dark:hover:bg-dark-border transition-colors ${activeMerker.has(row[nameField]) ? 'bg-primary-100 dark:bg-primary-900/30 border-l-4 border-primary-500' : idx % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''} ${onRowClick ? 'cursor-pointer' : ''}`}
                  onClick={() => onRowClick?.(row)}
                >
                  <td className="text-center">
                    {row.ranking !== null && (
                      <span className="text-gray-700 dark:text-gray-300">
                        {row.ranking}
                      </span>
                    )}
                  </td>
                  {showBrandColumn && <td className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">{row.brand || '—'}</td>}
                  <td className="max-w-[180px] truncate font-bold">{row[nameField]}</td>
                  <td className="text-right">{toNumber(row.current_count).toLocaleString('nb-NO')}</td>
                  <td className="text-right border-r-2 border-gray-300 dark:border-gray-600"><span className="text-gray-900 dark:text-gray-100">{toNumber(row.current_market_share).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})}</span></td>
                  <td className="text-right">{toNumber(row.previous_count).toLocaleString('nb-NO')}</td>
                  <td className="text-right border-r-2 border-gray-300 dark:border-gray-600"><span className="text-gray-600 dark:text-gray-400">{toNumber(row.previous_market_share).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})}</span></td>
                  <td className="text-right"><ChangeCount value={toNumber(row.change_count)} /></td>
                  <td className="text-right"><ChangeIndicator value={row.change_percent} showIcon={false} /></td>
                  {hasYtd && (
                    <>
                      <td className="text-right border-l-2 border-gray-200 dark:border-gray-700">{row.ytd_current_count !== null ? toNumber(row.ytd_current_count).toLocaleString('nb-NO') : '-'}</td>
                      <td className="text-right">{row.ytd_previous_count !== null ? toNumber(row.ytd_previous_count).toLocaleString('nb-NO') : '-'}</td>
                      <td className="text-right"><ChangeIndicator value={row.ytd_change_percent} showIcon={false} /></td>
                    </>
                  )}
                  {onRowClick && (
                    <td className="text-center">
                      <button
                        onClick={(e) => { e.stopPropagation(); onRowClick(row) }}
                        className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        <span className="text-xs font-medium">{actionLabel || t('total.seeDetails')}</span>
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {rows.length === 0 && (
          <p className="text-center text-gray-500 dark:text-gray-400 py-8">{t('total.noData')}</p>
        )}
      </div>
    )
  }

  // ====== RENDER ======

  const hasAnyData = brandsData.length > 0 || allModelsData.length > 0
  if (isLoading && !hasAnyData && !masterModelsData.length && !variantsData.length && !allVariantsData.length) return <LoadingSpinner />
  if (currentError && !hasAnyData) return <ErrorMessage message={currentError} />

  // Determine whether we're in a sub-level (drilled down) on either tab
  const isMerkeSubLevel = activeTab === 'merke' && selectedBrand
  const isModellSubLevel = activeTab === 'modell' && selectedModelAll

  return (
    <div className="space-y-6">
      {/* Header with breadcrumb */}
      <div>
        {isMerkeSubLevel ? (
          <div className="flex items-center gap-4">
            <button
              onClick={selectedMasterModel ? handleBackToMasterModels : handleBackToBrands}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-dark-card hover:bg-gray-200 dark:hover:bg-dark-border rounded-lg transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>{t('common.back')}</span>
            </button>
            <div>
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
                <span className="hover:text-primary-600 cursor-pointer" onClick={handleBackToBrands}>{t('total.title')}</span>
                <ChevronRight className="w-3 h-3" />
                {selectedMasterModel ? (
                  <>
                    <span className="hover:text-primary-600 cursor-pointer" onClick={handleBackToMasterModels}>{selectedBrand}</span>
                    <ChevronRight className="w-3 h-3" />
                    <span className="text-gray-900 dark:text-gray-100 font-medium">{selectedMasterModel.name}</span>
                  </>
                ) : (
                  <span className="text-gray-900 dark:text-gray-100 font-medium">{selectedBrand}</span>
                )}
              </div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                {selectedMasterModel
                  ? t('total.variantsHeading', { name: `${selectedBrand} ${selectedMasterModel.name}` })
                  : t('total.modelsHeading', { brand: selectedBrand })
                }
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                {selectedMasterModel
                  ? t('total.compareVariants')
                  : t('total.compareModels')
                }
              </p>
            </div>
          </div>
        ) : isModellSubLevel ? (
          <div className="flex items-center gap-4">
            <button
              onClick={handleBackToAllModels}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-dark-card hover:bg-gray-200 dark:hover:bg-dark-border rounded-lg transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>{t('common.back')}</span>
            </button>
            <div>
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
                <span className="hover:text-primary-600 cursor-pointer" onClick={handleBackToAllModels}>{t('total.title')}</span>
                <ChevronRight className="w-3 h-3" />
                <span className="text-gray-900 dark:text-gray-100 font-medium">{selectedModelAll.name}</span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                {t('total.variantsHeading', { name: selectedModelAll.name })}
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                {t('total.compareVariants')}
              </p>
            </div>
          </div>
        ) : (
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{t('total.title')}</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              {t('total.subtitle')}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{t('common.sourceData')}</p>
          </div>
        )}
      </div>

      {/* Output Mode Selector */}
      <div className="card p-4">
        <div className="flex flex-wrap items-center gap-3">
          <Calendar className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('total.period')}</span>
          <div className="flex flex-wrap gap-2">
            {OUTPUT_MODES.map(mode => (
              <button
                key={mode}
                onClick={() => setOutputMode(mode)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  outputMode === mode
                    ? 'bg-primary-600 text-white shadow-xs'
                    : 'bg-gray-100 dark:bg-dark-card text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-border'
                }`}
              >
                {t(`outputModes.${mode}`)}
              </button>
            ))}
          </div>
          {outputMode === 'custom' && (
            <div className="flex items-end gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('total.startDate')}
                </label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => {
                    const val = e.target.value
                    setCustomStartDate(maxDataDate && val > maxDataDate ? maxDataDate : val)
                  }}
                  max={maxDataDate || undefined}
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('total.endDate')}
                </label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => {
                    const val = e.target.value
                    setCustomEndDate(maxDataDate && val > maxDataDate ? maxDataDate : val)
                  }}
                  max={maxDataDate || undefined}
                  className="input-field"
                />
              </div>
            </div>
          )}
          <div className="ml-auto flex gap-2">
            <button
              onClick={() => {
                if (outputMode === 'custom' && (!customStartDate || !customEndDate)) return
                if (activeTab === 'merke') {
                  if (selectedBrand && selectedMasterModel) fetchVariants(selectedMasterModel.id)
                  else if (selectedBrand) fetchMasterModels(selectedBrand)
                  else fetchBrands()
                } else {
                  fetchBrands() // Keep brandsSummary in sync for TOTAL row
                  if (selectedModelAll) fetchAllVariants(selectedModelAll.id)
                  else fetchAllModels()
                }
              }}
              className={`btn-primary text-sm ${outputMode === 'custom' && (!customStartDate || !customEndDate) ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={outputMode === 'custom' && (!customStartDate || !customEndDate)}
            >
              {t('common.update')}
            </button>
          </div>
        </div>

        {/* Postal Code Filter - inside periode card */}
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-dark-border">
          <PostnummerFilter
            enabled={postnummerFilterEnabled}
            onToggle={setPostnummerFilterEnabled}
            compact
          />
        </div>
      </div>

      {/* Tabs */}
      {!isMerkeSubLevel && !isModellSubLevel && (
        <div className="flex gap-2 border-b border-gray-200 dark:border-dark-border">
          <button
            onClick={() => handleTabSwitch('merke')}
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              activeTab === 'merke'
                ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
                : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            <span className="flex items-center gap-2">
              <Car className="w-4 h-4" />
              {t('common.brand')}
            </span>
          </button>
          <button
            onClick={() => handleTabSwitch('modell')}
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              activeTab === 'modell'
                ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
                : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            <span className="flex items-center gap-2">
              <Layers className="w-4 h-4" />
              {t('common.model')}
            </span>
          </button>
        </div>
      )}

      {/* Period Info Banner — replaced by QueryExplanationBox which includes the same info */}
      {/* <PeriodInfoBanner
        periods={periods}
        maxDataDate={maxDataDate}
        outputMode={outputMode}
      /> */}

      {/* Query Explanation Box — uses queried (snapshotted) filter values, not live state */}
      <QueryExplanationBox
        periods={periods}
        maxDataDate={maxDataDate}
        outputMode={queriedOutputMode}
        vehicleFilter={queriedVehicleFilter as 'personbil' | 'varebil' | 'alle'}
        postnummerFilter={queriedPostnummer}
        drillDownLabel={
          activeTab === 'merke'
            ? selectedMasterModel
              ? t('total.variantsFor', { name: `${selectedBrand} ${selectedMasterModel.name}` })
              : selectedBrand
                ? t('total.modelsFor', { brand: selectedBrand })
                : null
            : selectedModelAll
              ? t('total.variantsFor', { name: selectedModelAll.name })
              : null
        }
      />

      {/* Summary Cards - removed per formatting update */}

      {/* Loading state. The full-page spinner above only covers the very first
          load (no data at all); every later fetch lands here. The Modell tab's
          own fetch has no selected* value, so gating on those left that tab
          blank for the whole request -- and it is the slowest one (limit=400). */}
      {isLoading && <LoadingSpinner />}

      {/* Error state, same reasoning: the tables hide themselves on error, so
          without this the user gets a blank tab and no explanation. */}
      {currentError && <ErrorMessage message={currentError} />}

      {/* ===== MERKE TAB ===== */}

      {/* Merke Level 3: Variants */}
      {activeTab === 'merke' && selectedBrand && selectedMasterModel && !variantsLoading && !variantsError && (
        renderComparisonTable(
          sortedVariants,
          variantsSummary,
          'model_variant',
          'Variant',
          variantsSearch,
          setVariantsSearch,
          t('total.searchVariant'),
          variantsSortConfig,
          setVariantsSortConfig,
          t('exportTitles.totalVariants', { brand: selectedBrand, model: selectedMasterModel.name, mode: outputMode }),
          undefined,
          undefined,
          variantsLoading,
        )
      )}

      {/* Merke Level 2: Master Models */}
      {activeTab === 'merke' && selectedBrand && !selectedMasterModel && !masterModelsLoading && !masterModelsError && (
        renderComparisonTable(
          sortedMasterModels,
          masterModelsSummary,
          'master_model',
          t('common.model'),
          masterModelsSearch,
          setMasterModelsSearch,
          t('total.searchModel'),
          masterModelsSortConfig,
          setMasterModelsSortConfig,
          t('exportTitles.totalModels', { brand: selectedBrand, mode: outputMode }),
          (row: TotalStatsMasterModelRow) => handleMasterModelClick(row),
          t('total.actionVariants'),
          masterModelsLoading,
        )
      )}

      {/* Merke Level 1: Brands */}
      {activeTab === 'merke' && !selectedBrand && !brandsLoading && !brandsError && (
        renderComparisonTable(
          sortedBrands,
          brandsSummary,
          'brand',
          t('common.brand'),
          brandsSearch,
          setBrandsSearch,
          t('common.searchBrand'),
          brandsSortConfig,
          setBrandsSortConfig,
          t('exportTitles.totalBrands', { mode: outputMode }),
          (row: TotalStatsBrandRow) => handleBrandClick(row.brand),
          t('total.actionModels'),
          brandsLoading,
        )
      )}

      {/* ===== MODELL TAB ===== */}

      {/* Modell tab: Variant drill-down */}
      {activeTab === 'modell' && selectedModelAll && !allVariantsLoading && !allVariantsError && (
        renderComparisonTable(
          sortedAllVariants,
          allVariantsSummary,
          'model_variant',
          'Variant',
          allVariantsSearch,
          setAllVariantsSearch,
          t('total.searchVariant'),
          allVariantsSortConfig,
          setAllVariantsSortConfig,
          t('exportTitles.totalAllVariants', { model: selectedModelAll.name, mode: outputMode }),
          undefined,
          undefined,
          allVariantsLoading,
        )
      )}

      {/* Modell tab: All models */}
      {activeTab === 'modell' && !selectedModelAll && !allModelsLoading && !allModelsError && (
        renderComparisonTable(
          sortedAllModels,
          brandsSummary as unknown as TotalStatsMasterModelRow,
          'master_model',
          t('common.model'),
          allModelsSearch,
          setAllModelsSearch,
          t('total.searchModel'),
          allModelsSortConfig,
          setAllModelsSortConfig,
          t('exportTitles.totalAllModels', { mode: outputMode }),
          (row: TotalStatsMasterModelRow) => handleModelAllClick(row),
          t('total.actionVariants'),
          allModelsLoading,
          true,
        )
      )}
    </div>
  )
}
